import { useCallback } from "react";

import clsx from "clsx";
import Image from "next/image";
import { useRouter } from "next/router";

import AIVoice from "@musicfy/assets/images/ai_voice.png";
import ArtistVocalsImage from "@musicfy/assets/images/artist_vocals.png";
import InstrumentalsImage from "@musicfy/assets/images/instrumentals.png";
import FavoritesImage from "@musicfy/assets/images/parody.png";
import Icon from "@musicfy/components/Icon";
import { Features } from "@musicfy/contants/Features";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { canUseFeature } from "@musicfy/utils";

import { EFeatures, FEATURE_ROUTES } from "../../Navigation/constants";
import { ModelCategory, type TModelCategory } from "../types";

const MODEL_CATEOGRIES = [
  {
    key: ModelCategory.VOCALS,
    label: "All Voices",
    image: ArtistVocalsImage,
    color1: "#FF9BA8",
    color2: "#E068DE",
  },
  {
    key: ModelCategory.COPYRIGHT_FREE,
    label: "Copyright-Free Voices",
    image: ArtistVocalsImage,
    color1: "#7DE8B5",
    color2: "#3592FF",
  },
  {
    key: ModelCategory.INSTRUMENTALS,
    label: "Instrumentals",
    image: InstrumentalsImage,
    color1: "#AB68E0",
    color2: "#3592FF",
  },
  {
    key: ModelCategory.TRENDING,
    label: "Trending Voices",
    image: ArtistVocalsImage,
    color1: "#FFB347",
    color2: "#FF6B6B",
  },
  {
    key: ModelCategory.FAVORITES,
    label: "Favorites",
    image: FavoritesImage,
    color1: "#688AE0",
    color2: "#9BFFFF",
  },
];

const MY_VOICE = {
  key: ModelCategory.MY_VOICES,
  label: "My Voices",
  image: AIVoice,
  color1: "#7DE8B5",
  color2: "#BA9C78",
};

const ModelsCategories = ({
  selectedModelsCategory,
  setSelectedModelsCategory,
}: {
  selectedModelsCategory: TModelCategory;
  setSelectedModelsCategory: (category: TModelCategory) => void;
}): JSX.Element => {
  const { subscription } = useSubscriptionContext();

  const router = useRouter();

  const canTrainVoice = canUseFeature(Features.MODEL_TRAINING, subscription);

  const categories = canTrainVoice
    ? [...MODEL_CATEOGRIES, MY_VOICE]
    : MODEL_CATEOGRIES;

  const onMakeYourOwnVoiceClick = useCallback(() => {
    router.push(FEATURE_ROUTES[EFeatures.VOICES]);
  }, [router]);

  return (
    <div className="grid xl:grid-cols-4 grid-cols-2 gap-5">
      {categories.map(({ key, label, image, color1, color2 }) => {
        const isSelectedCategory = selectedModelsCategory === key;

        return (
          <div
            role="button"
            tabIndex={0}
            aria-label={`${
              isSelectedCategory ? "Selected" : "Filter By"
            } ${label}`}
            onClick={() => setSelectedModelsCategory(key)}
            key={key}
            style={{
              outlineColor: isSelectedCategory ? color1 : "transparent",
            }}
            className={clsx(
              "rounded-8 group relative overflow-hidden outline outline-2 bg-gray-800 cursor-pointer transition-all",
              {
                "grayscale-0": isSelectedCategory,
                "grayscale-[.7] xl:hover:grayscale-[.2]": !isSelectedCategory,
              }
            )}
          >
            <div
              style={{
                background: `linear-gradient(to bottom right, ${color1}, ${color1}1A)`,
              }}
              className="absolute z-0 w-full h-full"
            />
            <div
              style={{
                background: `radial-gradient(circle at bottom right, ${color2}, ${color2}1A)`,
              }}
              className="absolute z-0 blur-2xl w-2/3 h-full right-0 bottom-0"
            />
            <div
              className={clsx(
                "absolute z-20 pointer-events-none bg-black w-full h-full transition-all",
                {
                  "opacity-20": isSelectedCategory,
                  "opacity-70 xl:group-hover:opacity-30": !isSelectedCategory,
                }
              )}
            />
            <div className="ml-auto w-fit relative z-10">
              <Image src={image} alt="filter image" />
            </div>
            <div className="absolute z-30 bottom-2 left-2 lg:bottom-4 lg:left-5">
              {label}
            </div>
          </div>
        );
      })}
      {!canTrainVoice && (
        <div
          role="button"
          aria-label="Create your own AI Voice"
          tabIndex={0}
          onClick={onMakeYourOwnVoiceClick}
          className="rounded-8 group relative overflow-hidden bg-gray-800 cursor-pointer transition-all grayscale-[.7] xl:hover:grayscale-0"
        >
          <div
            style={{
              background: `linear-gradient(to bottom right, #7DE8B5, #7DE8B51A)`,
            }}
            className="absolute z-0 w-full h-full"
          />
          <div
            style={{
              background: `radial-gradient(circle at bottom right, #BA9C78, #7DE8B5)`,
            }}
            className="absolute z-0 blur-2xl w-full h-full "
          />
          <div className="absolute z-20 pointer-events-none bg-black opacity-50 xl:group-hover:opacity-20 w-full h-full transition-all" />
          <div className="ml-auto relative z-10 w-fit">
            <Image src={AIVoice} alt="ai image" />
          </div>
          <div className="absolute z-30 bottom-1 left-2 lg:bottom-2 lg:left-5 lg:w-1/2">
            Make Your Own AI Voice
          </div>
          <div className="absolute z-20 top-2 left-2">
            <Icon name="crown" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelsCategories;
